import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as api from '../lib/api'
import { DATE_FORMAT_LABEL, formatDate } from '../lib/dateFormat'
import {
  getConfiguredFactorRatingOptions,
  getConfiguredObjectiveRow,
  getConfiguredOverallRatingOptions,
  hasObjectiveEntry as hasConfiguredObjectiveEntry,
  hasObjectiveText as hasConfiguredObjectiveText,
  isConfiguredFormSectionEnabled,
  objectivePriorityTotal,
  useFormStructure,
  usesConfiguredAppraisalLayout,
  ratingOptionText
} from './useFormStructure'
import { Card, Input, Select, Button, Spin, Alert, Typography, Space, Row, Col, message, Modal, Collapse } from 'antd'
import Popconfirm from '../components/Popconfirm'
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { businessUnitThemeVars, getBusinessUnitTheme } from './businessUnitTheme'
import { getConfiguredRatingDisplay, getConfiguredRatingFullLabel, getConfiguredRatingOptions, getEnabledRatingOptions, getRatingGuideData, getRatingOptionLabel, getRatingValueDisplay, usesRatingGuideTable } from './ratingScale'
import RatingGuideTable from './RatingGuideTable'
import BackButton from './BackButton'
import AppraisalConversationSidebar from './AppraisalConversationSidebar'
import ManagerFeedback from './ManagerFeedback'
import { getPrcSuccessIndicatorValue, isPrcAppraisal, prcEmployeeInformationFields, prcEmployeeInformationTitle, prcEvaluationLevel, prcIdpFields, prcObjectiveConfig, prcObjectiveScoreLabel, prcObjectiveScoreMax, prcSuccessIndicatorField } from './prcFlow'
import PrcRatingGuideDescription from './PrcRatingGuideDescription'
import { CultureValuesCheckboxes, checkboxRatingGuideSource, isMilAsiaCheckboxFlow, setCheckboxFlowData } from './milAsiaCheckboxFlow'
import PerformanceFactorsCard from './PerformanceFactorsCard'
import { MilwaukeeAsiaEnglishSections, isConfiguredRegionalForm, renderBoldMarkup, validateMilwaukeeAsiaEnglishEmployeeSections } from './milwaukeeAsiaEnglish'
import { approverName, personLabel } from './HRDashboard'
import { competencySuggestedRating, objectiveSuggestedRating, overallAppraisalAverage, overallSuggestedRating, weightedObjectiveAverage } from './objectiveScores'
import { requiresVietnamMilwaukeeJustification, vietnamMilwaukeeJustificationMinLength } from './vietnamMilwaukeeJustification'
import SuggestedRatingBand from './SuggestedRatingBand'
import CalculatedRatingBand from './CalculatedRatingBand'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select

const hasObjectiveFieldValue = value => (
  value !== null &&
  value !== undefined &&
  (typeof value !== 'string' || value.trim() !== '')
)

const hasAnnualObjectiveContent = objective => (
  ['objective', 'success_indicators', 'self_score', 'self_comment', 'manager_score', 'manager_comment']
    .some(field => hasObjectiveFieldValue(objective?.[field])) ||
  Object.entries(objective || {}).some(([field, value]) => (
    field.startsWith('success_indicator_') && hasObjectiveFieldValue(value)
  ))
)

const hasAnnualObjectiveEntry = objective => (
  hasAnnualObjectiveContent(objective) ||
  (
    objective?.priority !== null &&
    objective?.priority !== undefined &&
    `${objective.priority}`.trim() !== ''
  )
)

const getRatingCategory = (value) => {
  const rating = parseFloat(value)
  if (!rating) return null
  if (rating <= 1.5) return { code: 'NI', rating: 'Needs Improvement' }
  if (rating <= 1.75) return { code: 'ME-', rating: 'Meets Expectations-' }
  if (rating <= 2.25) return { code: 'ME', rating: 'Meets Expectations' }
  if (rating <= 2.5) return { code: 'ME+', rating: 'Meets Expectations+' }
  if (rating <= 2.75) return { code: 'EE-', rating: 'Exceeds Expectations-' }
  if (rating <= 3.25) return { code: 'EE', rating: 'Exceeds Expectations' }
  if (rating <= 3.5) return { code: 'EE+', rating: 'Exceeds Expectations+' }
  return { code: 'E', rating: 'Excels' }
}

const getRatingCategoryDisplay = (value) => {
  const category = getRatingCategory(value)
  return category ? `${category.code} - ${category.rating}` : null
}

const RatingGuideDescription = ({ source }) => {
  const { conversionGuide, descriptions } = getRatingGuideData(source)
  if (usesRatingGuideTable(source)) return <RatingGuideTable source={source} />

  return (
    <div className='tti-rating-guide'>
      <Text strong className='tti-rating-guide-heading'>Performance Rating Conversion</Text>
      <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
        {conversionGuide.map(item => (
          <Col span={6} key={item.code}>
            <Text strong>{item.code}:</Text> {item.rating} ({item.range})
          </Col>
        ))}
      </Row>

      <div style={{ marginTop: 16 }}>
        <Text strong className='tti-rating-guide-heading'>Performance Rating Description</Text>
        <div style={{ marginTop: 8 }}>
          {descriptions.map(item => (
            <div key={item.code} style={{ marginBottom: 8 }}>
              <Text strong>{item.code} ({item.score}) - {item.rating}:</Text> {item.description}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Form structure with categories and questions
const FORM_STRUCTURE = [
  {
    id: 'quality_of_work',
    title: 'Category 1: Quality of Work',
    questions: [
      { id: 'q1', text: 'Pursues quality and achieves excellence in all tasks and goals.' },
      { id: 'q2', text: 'Takes initiative and produces work consistent with defined organizational standards for the position.' },
      { id: 'q3', text: 'Diligently attends to detail; performs tasks with accuracy, completeness and high quality.' },
      { id: 'q4', text: 'Addresses problems and overcomes obstacles successfully to timely completion of tasks.' },
      { id: 'q5', text: 'Demonstrates self-reliance and accomplishes work objectives with minimal supervision.' }
    ]
  },
  {
    id: 'work_attitude',
    title: 'Category 2: Work Attitude',
    questions: [
      { id: 'q1', text: 'Demonstrates the attitude of ownership, and creativity to accomplish tasks.' },
      { id: 'q2', text: 'Takes ownership for the success and quality of work produced.' },
      { id: 'q3', text: 'Accepts responsibilities for own behaviors, faces up to problems, acknowledges and corrects mistakes.' },
      { id: 'q4', text: 'Embraces diverse perspectives to promote or nurture innovation.' },
      { id: 'q5', text: 'Generates new ideas, solutions and creative approaches.' }
    ]
  },
  {
    id: 'knowledge',
    title: 'Category 3: Knowledge',
    questions: [
      { id: 'q1', text: 'Possesses requisite knowledge and skills for assigned roles and responsibilities. Willing to acquire new skills for self development and advancement.' },
      { id: 'q2', text: 'Demonstrates relevant job knowledge and essential skills in performing duties and functions of the position.' },
      { id: 'q3', text: 'Displays a high understanding of all work practices, policies, and procedures.' },
      { id: 'q4', text: 'Actively participates in appropriate learning and training activities that expand job knowledge and effectiveness.' },
      { id: 'q5', text: 'Shares information and expertise to assist others to accomplish work assignments.' }
    ]
  },
  {
    id: 'communication',
    title: 'Category 4: Communication',
    questions: [
      { id: 'q1', text: 'Listens to others and communicates with clarity and effectiveness.' },
      { id: 'q2', text: 'Provides regular, consistent and meaningful information and ideas with others to make sure all appropriate individuals are informed.' },
      { id: 'q3', text: 'Communicates in a clear and concise manner using appropriate language, tone, and structure.' },
      { id: 'q4', text: 'Listens carefully to others, opens to other points of view, asks questions for clarification and ensures what others have said is understood.' },
      { id: 'q5', text: 'Gives and receives constructive feedback to foster effective working relationships.' }
    ]
  },
  {
    id: 'planning_organizing',
    title: 'Category 5: Planning & Organizing',
    questions: [
      { id: 'q1', text: 'Identifies and priorities tasks, formulates and executes plans to achieve objectives.' },
      { id: 'q2', text: 'Manages multiple duties and tasks effectively and efficiently.' },
      { id: 'q3', text: 'Identifies and organizes the varied resources needed to achieve goals and prioritizes key action steps.' },
      { id: 'q4', text: 'Prepares clear plans with strategic focus, specific milestones and deadlines prior to initiative project work.' },
      { id: 'q5', text: 'Develops contingency plans for potential problems or drastic changes.' }
    ]
  },
  {
    id: 'interaction_with_others',
    title: 'Category 6: Interaction with Others',
    questions: [
      { id: 'q1', text: 'Positively interacts with a diverse workforce.' },
      { id: 'q2', text: 'Works with team members and others in ways that strengthen optimal team interaction and results.' },
      { id: 'q3', text: 'Encourages and supports colleagues in expressing ideas and sharing information.' },
      { id: 'q4', text: 'Cultivates positive relationships among team members by actively contributing to team processes and deliverables, such as team meetings and assignments.' },
      { id: 'q5', text: 'Demonstrates understanding, courtesy, empathy and genuine concern in all interactions.' }
    ]
  }
]

const DEFAULT_OBJECTIVE_ROW = { priority: '', objective: '', success_indicators: '', self_score: null, self_comment: '', manager_score: null, manager_comment: '' }

const isScoreOutsideObjectiveRange = (value, maxScore = 4, allowDecimals = false) => {
  if (value === null || value === undefined || value === '') return false
  const score = Number(value)
  return !Number.isFinite(score) || (!allowDecimals && !Number.isInteger(score)) || score < 1 || score > maxScore
}

const normalizeObjectiveScore = (value, maxScore = 4, allowDecimals = false) => {
  if (value === null || value === undefined || value === '') return ''
  const score = parseFloat(value)
  if (Number.isNaN(score)) return ''
  if (score < 1) return 1
  if (score > maxScore) return maxScore
  return allowDecimals ? score : Math.round(score)
}

const isWholeNumberInput = value => value === '' || /^\d+$/.test(value)
const isDecimalInput = value => value === '' || /^\d*(?:\.\d*)?$/.test(value)

export default function EmployeeForm({ appraisal: providedAppraisal = null, translationReview = false }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [appraisal, setAppraisal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [formData, setFormData] = useState({})
  const [employeeOverallComment, setEmployeeOverallComment] = useState('')
  const [resubmissionRemarks, setResubmissionRemarks] = useState('')
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const initializedFeedbackAppraisal = useRef(null)
  const [objectives, setObjectives] = useState(Array.from({ length: 3 }, () => ({ ...DEFAULT_OBJECTIVE_ROW })))
  const [nextYearObjectives, setNextYearObjectives] = useState([])
  const firstApproverName = approverName(appraisal, 1)

  useEffect(() => {
    const applyAppraisal = a => {
      const appraisalKey = a.id ?? id
      if (initializedFeedbackAppraisal.current !== appraisalKey) {
        initializedFeedbackAppraisal.current = appraisalKey
        setFeedbackOpen(Boolean(a.return_comments || a.return_history?.length))
      }
      setAppraisal(a)
      setFormData(a.form_data || {})
      setEmployeeOverallComment(a.employee_overall_comment || '')
      const existingObjectives = a.form_data?.objectives?.length
        ? a.form_data.objectives
        : (a.form_data?.annual_objectives || [])
      if (existingObjectives.length > 0) {
        setObjectives(existingObjectives)
      } else if (translationReview) {
        setObjectives(Array.from({ length: 3 }, () => ({ ...DEFAULT_OBJECTIVE_ROW })))
      }
      setNextYearObjectives(
        a.form_data?.next_year_objectives?.length
          ? a.form_data.next_year_objectives
          : Array.from({ length: 3 }, () => ({ priority: '', objective: '' }))
      )
    }
    if (providedAppraisal) {
      applyAppraisal(providedAppraisal)
      setLoading(false)
      return
    }
    api.getAppraisal(id).then(applyAppraisal).finally(() => setLoading(false))
  }, [id, providedAppraisal, translationReview])
  const {
    formStructure,
    formConfig,
    loading: structureLoading,
    error: structureError
  } = useFormStructure(
    appraisal?.session_template || appraisal?.session_business_unit,
    FORM_STRUCTURE,
    { translationReview }
  )
  const templateCode = appraisal?.session_template || appraisal?.session_business_unit || ''
  const objectivesConfig = formConfig?.objectives || {}
  const showObjectiveAverageScore = objectivesConfig.show_average_score !== false
  const showCategoryAverageAsRating = formConfig?.competencies?.category_average_display === 'rating_name'
  const categoryAverageLabel = formConfig?.competencies?.category_average_label || 'Calculated Rating'
  const competencySummaryTitle = formConfig?.competencies?.summary_title || 'Competency Average Score'
  const competencySummarySubtitle = formConfig?.competencies?.summary_subtitle
  const singleCategoryComment = templateCode === 'vietnam_milwaukee' && formConfig?.competencies?.single_category_comment === true
  const categoryCommentQuestionKey = formConfig?.competencies?.category_comment_question_key || 'q1'
  const categoryCommentLabel = formConfig?.competencies?.category_comment_label || 'Comment'
  const categoryCommentRequired = singleCategoryComment && formConfig?.competencies?.category_comment_required === true
  const overallPerformanceTitle = formConfig?.overall_performance?.summary_title || 'Overall Average Score'
  const competencyManualRating = formConfig?.competencies?.manual_rating || {}
  const overallManualRating = formConfig?.overall_performance?.manual_rating || {}
  const employeeCompetencyRatingField = competencyManualRating.employee_field || 'employee_competency_overall_rating'
  const employeeOverallRatingField = overallManualRating.employee_field || 'employee_overall_performance_rating'
  const isOverallPerformanceRatingMissing = showValidationErrors &&
    overallManualRating.enabled &&
    overallManualRating.required &&
    !formData[employeeOverallRatingField]
  const showCompetencySummarySubtext = formConfig?.competencies?.show_summary_subtext !== false
  const showOverallSummarySubtext = formConfig?.overall_performance?.show_summary_subtext !== false
  const objectivePriorityOptions = Array.isArray(objectivesConfig.priority_options) ? objectivesConfig.priority_options : []
  const usesPriorityOptions = objectivePriorityOptions.length > 0
  const allowedPriorityValues = new Set(objectivePriorityOptions.map(option => `${option.value}`))
  const isLegacyNumericPriority = value => value !== '' && value !== null && value !== undefined && !Number.isNaN(Number(value))
  useEffect(() => {
    if (
      appraisal &&
      !structureLoading &&
      usesConfiguredAppraisalLayout(formConfig) &&
      !(appraisal.form_data?.objectives?.length || appraisal.form_data?.annual_objectives?.length)
    ) {
      setObjectives(Array.from({ length: 3 }, () => ({ ...getConfiguredObjectiveRow(formConfig) })))
    }
  }, [appraisal, formConfig, structureLoading])
  const usesConfiguredRatings = Array.isArray(formConfig.rating_options) && formConfig.rating_options.length > 0
  const allowDecimalObjectiveScores = templateCode === 'vietnam_tti' || objectivesConfig.allow_decimal_scores === true
  const usesConfiguredObjectiveRatings = usesConfiguredRatings && !allowDecimalObjectiveScores
  const getQuestionRatingOptions = (currentValue) => usesConfiguredRatings
    ? getConfiguredRatingOptions(formConfig, currentValue)
    : getEnabledRatingOptions(
      checkboxRatingGuideSource(appraisal, formConfig),
      currentValue === null || currentValue === undefined || currentValue === '' ? [] : [currentValue]
    )
  const getQuestionRatingLabel = option => usesConfiguredRatings ? option.label : getRatingOptionLabel(option)
  const getEmployeeRatingOptionLabel = option => (
    templateCode === 'vietnam_milwaukee'
      ? option.label
      : `${option.value} - ${option.label}`
  )
  const isObjectiveScoreInvalid = value => {
    if (value === null || value === undefined || value === '') return false
    return usesConfiguredObjectiveRatings
      ? !getConfiguredRatingOptions(formConfig).some(option => option.value === Number(value))
      : isScoreOutsideObjectiveRange(value, prcObjectiveScoreMax(appraisal, formConfig), allowDecimalObjectiveScores)
  }

  const updateField = (categoryId, questionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [questionId]: {
          ...prev[categoryId]?.[questionId],
          [field]: value
        }
      }
    }))
  }

  const getFieldValue = (categoryId, questionId, field) => {
    return formData[categoryId]?.[questionId]?.[field] || (field.includes('rating') ? null : '')
  }

  const calculateCategoryAverage = (categoryId, ratingField) => {
    const category = formStructure.find(c => c.id === categoryId)
    if (!category) return null

    const ratings = category.questions.map(q => getFieldValue(categoryId, q.id, ratingField))
    if (ratings.length === 0 || ratings.some(rating => rating === null || rating === '' || rating === undefined)) return null

    const sum = ratings.reduce((acc, rating) => acc + parseFloat(rating), 0)
    return (sum / ratings.length).toFixed(2)
  }

  const calculateOverallCategoryAverage = (ratingField) => {
    const categoryAverages = formStructure.map(category => calculateCategoryAverage(category.id, ratingField))
    if (categoryAverages.length === 0 || categoryAverages.some(average => average === null)) return null
    return (categoryAverages.reduce((sum, average) => sum + parseFloat(average), 0) / categoryAverages.length).toFixed(2)
  }

  const categoryRatings = (categoryId, ratingField) => {
    const category = formStructure.find(item => item.id === categoryId)
    return category?.questions.map(question => getFieldValue(categoryId, question.id, ratingField)) || []
  }

  const allCompetencyRatings = ratingField => (
    formStructure.flatMap(category => categoryRatings(category.id, ratingField))
  )

  const calculateCategorySuggestedRating = (categoryId, ratingField) => competencySuggestedRating(
    categoryRatings(categoryId, ratingField),
    calculateCategoryAverage(categoryId, ratingField),
    formConfig
  )

  const updateObjective = (index, field, value) => {
    setObjectives(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row))
  }
  const updateNextYearObjective = (index, field, value) => {
    setNextYearObjectives(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row))
  }
  const addObjectiveRow = () => setObjectives(prev => [...prev, { ...DEFAULT_OBJECTIVE_ROW }])
  const removeObjectiveRow = index => setObjectives(prev => prev.filter((_, rowIndex) => rowIndex !== index))
  const removeNextYearObjectiveRow = index => setNextYearObjectives(prev => prev.filter((_, rowIndex) => rowIndex !== index))

  const totalPriority = objectives.reduce((sum, o) => sum + (parseFloat(o.priority) || 0), 0)
  const hasAnyObjectiveText = objectives.some(hasConfiguredObjectiveText)

  const objectiveSelfAvg = weightedObjectiveAverage(
    objectives,
    'self_score',
    objectivesConfig.average_mode,
    objectivesConfig.allow_partial_average
  )
  const objectiveSummaryConfig = objectivesConfig.summary_rating || {}
  const objectiveSummaryEnabled = Boolean(objectiveSummaryConfig.enabled)
  const employeeObjectiveRatingField = objectiveSummaryConfig.employee_field || 'employee_objective_overall_rating'
  const employeeObjectiveSuggestedRating = objectiveSuggestedRating(
    objectives,
    'self_score',
    objectiveSelfAvg,
    objectivesConfig
  )

  const overallEmployeeCategoryAvg = calculateOverallCategoryAverage('employee_rating')
  const overallEmployeeAverage = overallAppraisalAverage(objectiveSelfAvg, overallEmployeeCategoryAvg)
  const employeeCompetencySuggestedRating = competencySuggestedRating(
    allCompetencyRatings('employee_rating'),
    overallEmployeeCategoryAvg,
    formConfig
  )
  const employeeOverallSuggestedRating = overallSuggestedRating(
    employeeObjectiveSuggestedRating,
    employeeCompetencySuggestedRating,
    overallEmployeeAverage,
    formConfig
  )
  const selectedEmployeeOverallRating = getConfiguredRatingOptions(formConfig, formData[employeeOverallRatingField]).find(
    option => option.value === Number(formData[employeeOverallRatingField])
  )
  const requiresOverallRatingJustification = (
    templateCode === 'vietnam_milwaukee' &&
    requiresVietnamMilwaukeeJustification(
      employeeOverallSuggestedRating,
      selectedEmployeeOverallRating?.label || selectedEmployeeOverallRating?.value
    )
  )
  const justificationMinLength = vietnamMilwaukeeJustificationMinLength(appraisal)

  const validateForm = () => {
    const resubmissionErrors = appraisal?.status === 'pending_employee_review' && !resubmissionRemarks.trim()
      ? ['Response to Approver 1 is required.']
      : []
    if (usesConfiguredAppraisalLayout(formConfig)) {
      const configuredObjectives = objectives.filter(hasConfiguredObjectiveEntry)
      const configuredPriorityTotal = objectivePriorityTotal(configuredObjectives)
      const missing = [...resubmissionErrors]
      const oneLineLabel = value => (value || '').replace(/\s*\n\s*/g, ' ').trim()
      const objectivesLabel = oneLineLabel(formConfig.objectives?.title) || 'Appraisal Year Objectives'
      const nextYearLabel = oneLineLabel(formConfig.next_year_objectives?.title) || "Next Year's Objectives"
      const selfRatingLabel = oneLineLabel(formConfig.appraisal_process?.employee_evaluation_label) || 'Employee Self-Rating'
      if (!configuredObjectives.some(hasConfiguredObjectiveText)) missing.push(`${objectivesLabel}: at least one objective is required.`)
      configuredObjectives.forEach((objective, index) => {
        if (!hasConfiguredObjectiveText(objective)) {
          missing.push(`${objectivesLabel} → Row ${objectives.indexOf(objective) + 1}: an objective is required.`)
        } else if (isConfiguredRegionalForm(formConfig) && !(objective.achievement_examples || '').trim()) {
          missing.push(`${objectivesLabel} → Row ${objectives.indexOf(objective) + 1}: examples of achievement are required.`)
        }
      })
      if (Math.abs(configuredPriorityTotal - 100) >= 0.01) {
        missing.push(`${objectivesLabel} → Total Priority must equal 100%. Current total is ${configuredPriorityTotal}%.`)
      }
      const configuredForm = formConfig.appraisal_form || {}
      if (
        isConfiguredRegionalForm(formConfig) &&
        !formData[configuredForm.employee_overall_rating_field || 'employee_overall_rating']
      ) {
        missing.push(`${selfRatingLabel} is required.`)
      }
      if (isConfiguredRegionalForm(formConfig)) {
        const nextObjectives = nextYearObjectives.filter(hasConfiguredObjectiveEntry)
        const nextPriorityTotal = objectivePriorityTotal(nextObjectives)
        const nextYearMax = formConfig.next_year_objectives?.maximum_rows
        if (!nextObjectives.some(hasConfiguredObjectiveText)) {
          missing.push(`${nextYearLabel}: at least one objective is required.`)
        }
        if (nextYearMax && nextObjectives.length > nextYearMax) {
          missing.push(`${nextYearLabel}: no more than ${nextYearMax} objectives are allowed.`)
        }
        nextObjectives.forEach(objective => {
          if (!hasConfiguredObjectiveText(objective)) {
            missing.push(`${nextYearLabel} → Row ${nextYearObjectives.indexOf(objective) + 1}: an objective is required.`)
          }
        })
        if (Math.abs(nextPriorityTotal - 100) >= 0.01) {
          missing.push(`${nextYearLabel} → Total Weighting must equal 100%. Current total is ${nextPriorityTotal}%.`)
        }
      }
      missing.push(...validateMilwaukeeAsiaEnglishEmployeeSections(formData, formConfig))
      return missing
    }

    const missing = [...resubmissionErrors]
    const activeObjectives = objectives.filter(hasAnnualObjectiveEntry)
    if (activeObjectives.length === 0) {
      missing.push('Annual Objectives → At least one Annual Performance Objective is required.')
    } else {
      activeObjectives.forEach((objective, index) => {
        if (!hasConfiguredObjectiveText(objective)) {
          missing.push(`Annual Objectives → Row ${objectives.indexOf(objective) + 1}: Annual Performance Objective is required.`)
        }
      })
    }
    if (usesPriorityOptions) {
      objectives.forEach((objective, index) => {
        if (
          hasAnnualObjectiveContent(objective) &&
          !allowedPriorityValues.has(`${objective.priority}`) &&
          !isLegacyNumericPriority(objective.priority)
        ) {
          missing.push(`Annual Objectives → Row ${index + 1}: select ${objectivePriorityOptions.map(option => option.label || option.value).join(' or ')} as the priority.`)
        }
      })
    } else {
      objectives.forEach((objective, index) => {
        const priority = Number(objective.priority)
        if (
          hasAnnualObjectiveContent(objective) &&
          (
            objective.priority === null ||
            objective.priority === undefined ||
            objective.priority === '' ||
            !Number.isInteger(priority) ||
            priority < 0 ||
            priority > 100
          )
        ) {
          missing.push(`Annual Objectives → Row ${index + 1}: priority must be a whole number from 0 to 100.`)
        }
      })
      if (Math.abs(totalPriority - 100) >= 0.01) {
        missing.push(`Annual Objectives → Total Priority must equal 100%. Current total is ${totalPriority}%.`)
      }
    }
    objectives.forEach((objective, index) => {
      if (hasConfiguredObjectiveText(objective) && (
        objective.self_score === null ||
        objective.self_score === undefined ||
        objective.self_score === ''
      )) {
        missing.push(`Annual Objectives → Row ${index + 1}: Self-evaluation rating is required.`)
      }
      if (isObjectiveScoreInvalid(objective.self_score)) {
        missing.push(`Annual Objectives → Row ${index + 1}: Self-evaluation score must be between 1 and ${prcObjectiveScoreMax(appraisal, formConfig)}.`)
      }
    })
    for (const category of formStructure) {
      for (const question of category.questions) {
        const rating = getFieldValue(category.id, question.id, 'employee_rating')
        if (!rating) missing.push(`${category.title} → Q${category.questions.indexOf(question) + 1}: Rating required`)
        if (isPrcAppraisal(appraisal) && isScoreOutsideObjectiveRange(rating, 10)) {
          missing.push(`${category.title} → Q${category.questions.indexOf(question) + 1}: Rating must be between 1 and 10.`)
        }
      }
      if (
        singleCategoryComment &&
        categoryCommentRequired &&
        !String(getFieldValue(category.id, categoryCommentQuestionKey, 'employee_comments') || '').trim()
      ) {
        missing.push(`${category.title} → ${categoryCommentLabel} is required.`)
      }
    }
    if (isPrcAppraisal(appraisal)) {
      prcIdpFieldRows.forEach(([field, label]) => {
        const managerOwned = field === 'supervisor_year_end_suggestion'
        if (!managerOwned && !(prcIdp[field] || '').trim()) {
          missing.push(`Individual Development Plan (IDP) → ${label} is required.`)
        }
      })
    }
    if (!employeeOverallComment.trim()) missing.push('Overall Employee Comment is required')
    if (
      objectiveSummaryEnabled &&
      objectiveSummaryConfig.required &&
      !formData[employeeObjectiveRatingField]
    ) {
      missing.push('Annual Objectives → Overall Rating of Objective Appraisal is required.')
    }
    if (competencyManualRating.enabled && competencyManualRating.required && !formData[employeeCompetencyRatingField]) {
      missing.push(`${competencySummaryTitle} is required.`)
    }
    if (overallManualRating.enabled && overallManualRating.required && !formData[employeeOverallRatingField]) {
      missing.push(`${overallPerformanceTitle} is required.`)
    }
    if (
      requiresOverallRatingJustification &&
      (formData.employee_overall_rating_justification || '').trim().length < justificationMinLength
    ) {
      missing.push(`${overallPerformanceTitle} justification must be at least ${justificationMinLength} characters.`)
    }
    return missing
  }

  const getDraftPayload = () => {
    const isConfiguredLayout = usesConfiguredAppraisalLayout(formConfig)
    const configuredFormData = {
      objectives: objectives.filter(hasConfiguredObjectiveEntry)
    }
    if (isConfiguredRegionalForm(formConfig)) {
      configuredFormData.next_year_objectives = nextYearObjectives.filter(hasConfiguredObjectiveEntry)
    }
    return {
      form_data: {
        ...formData,
        ...(isConfiguredLayout
          ? configuredFormData
          : { annual_objectives: objectives })
      },
      employee_overall_comment: employeeOverallComment,
      ...(isEmployeeReview ? { resubmission_remarks: resubmissionRemarks } : {})
    }
  }

  async function saveDraft() {
    const maxScore = prcObjectiveScoreMax(appraisal, formConfig)
    const invalidScoreIndex = objectives.findIndex(objective => isObjectiveScoreInvalid(objective.self_score))
    if (invalidScoreIndex >= 0) {
      message.error(`Annual Objectives row ${invalidScoreIndex + 1}: Self-evaluation score must be between 1 and ${maxScore}.`)
      return
    }
    setSavingDraft(true)
    try {
      const updated = await api.employeeDraft(id, getDraftPayload())
      setAppraisal(updated)
      message.success('Draft saved')
    } catch (e) {
      message.error(e.message || 'Failed to save draft')
    } finally {
      setSavingDraft(false)
    }
  }

  async function submit() {
    const errors = validateForm()
    if (errors.length > 0) {
      if (isEmployeeReview && !resubmissionRemarks.trim()) setFeedbackOpen(true)
      setShowValidationErrors(true)
      Modal.error({
        title: 'Please complete all fields before submitting',
        width: 720,
        content: (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 12 }}>
            <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
              {errors.map((error, index) => (
                <li key={index} style={{ marginBottom: 8 }}>{error}</li>
              ))}
            </ol>
          </div>
        )
      })
      return
    }
    setSubmitting(true)
    try {
      await api.employeeSubmit(id, getDraftPayload())
      navigate('/dashboard')
    } catch (e) {
      message.error(e.message)
    }
    setSubmitting(false)
  }

  if (loading || structureLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size='large' /></div>
  if (!appraisal) return <Alert type='error' message='Appraisal not found' />
  if (structureError) {
    return <Alert type='error' showIcon message='Unable to load the appraisal template' description='Refresh the page or contact HR if the problem continues.' />
  }

  const theme = getBusinessUnitTheme(appraisal)
  const isConfiguredLayout = usesConfiguredAppraisalLayout(formConfig)
  const isEmployeeReview = appraisal.status === 'pending_employee_review'
  const isEmployeeInitialEditable = translationReview || appraisal.status === 'pending_employee' || isEmployeeReview
  const isReadOnly = !isEmployeeInitialEditable
  const busy = savingDraft || submitting
  const showConversation = !translationReview && Boolean(
    appraisal.return_comments || appraisal.return_history?.length || isEmployeeReview
  )
  const isPrc = isPrcAppraisal(appraisal)
  const objectiveScoreMax = prcObjectiveScoreMax(appraisal, formConfig)
  const objectiveScoreLabel = prcObjectiveScoreLabel(appraisal, formConfig)
  const prcObjectives = prcObjectiveConfig(formConfig)
  const prcSuccessIndicatorLevels = prcObjectives.success_indicator_levels || []
  const prcIdpFieldRows = prcIdpFields(formConfig)
  const prcEmployeeInfoFields = prcEmployeeInformationFields(appraisal, formConfig)
  const prcIdp = formData.prc_idp || {}
  const isCheckboxFlow = isMilAsiaCheckboxFlow(appraisal, formConfig)
  const ratingGuideSource = checkboxRatingGuideSource(appraisal, formConfig)
  const feedbackRatingDisplay = value => isConfiguredLayout || usesConfiguredRatings
    ? getConfiguredRatingDisplay(value, formConfig)
    : getRatingValueDisplay(value, ratingGuideSource)
  const persistedFormData = appraisal.form_data || {}
  const persistedObjectives = persistedFormData.objectives?.length
    ? persistedFormData.objectives
    : (persistedFormData.annual_objectives || [])
  const conversationSidebar = showConversation && (
    <AppraisalConversationSidebar
      appraisal={appraisal}
      feedback={(
        <ManagerFeedback
          appraisal={appraisal}
          formData={persistedFormData}
          formStructure={formStructure}
          objectives={persistedObjectives}
          formConfig={formConfig}
          ratingDisplay={feedbackRatingDisplay}
        />
      )}
      composer={isEmployeeReview && !isReadOnly ? (
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Feedback to {firstApproverName}</Text>
          <TextArea
            aria-label={`Feedback to ${firstApproverName}`}
            rows={3}
            placeholder='Please Enter'
            value={resubmissionRemarks}
            onChange={event => setResubmissionRemarks(event.target.value)}
            disabled={isReadOnly || busy}
            status={showValidationErrors && !resubmissionRemarks.trim() ? 'error' : undefined}
          />
        </div>
      ) : null}
      actions={isEmployeeReview && !isReadOnly ? (
        <Button type='link' onClick={submit} loading={submitting} disabled={busy}>Send</Button>
      ) : null}
      open={feedbackOpen}
      onOpen={() => setFeedbackOpen(true)}
      onClose={() => setFeedbackOpen(false)}
    />
  )
  const actionBar = !isReadOnly && !translationReview && (
    <div className='tti-form-action-bar tti-appraisal-action-bar'>
      <div className='tti-form-action-bar__inner'>
        <Button
          className='tti-btn-secondary tti-form-action-bar__cancel'
          onClick={() => navigate('/dashboard')}
          disabled={busy}
        >
          Cancel
        </Button>
        <Space wrap className='tti-appraisal-actions'>
          <Button onClick={saveDraft} loading={savingDraft} disabled={busy} className='tti-btn-secondary'>Save Draft</Button>
          <Popconfirm
            title='Are you sure?'
            description='Please confirm you want to submit.'
            okText='Submit Appraisal'
            cancelText='Cancel'
            onConfirm={submit}
            disabled={busy}
          >
            <Button type='primary' loading={submitting} disabled={busy} style={{ color: '#FFFFFF' }}>
              Submit Appraisal
            </Button>
          </Popconfirm>
        </Space>
      </div>
    </div>
  )
  const updatePrcIdp = (field, value) => setFormData(prev => ({
    ...prev,
    prc_idp: {
      ...(prev.prc_idp || {}),
      [field]: value
    }
  }))

  if (isConfiguredLayout) {
    const configuredObjectives = objectives
    const configuredPriorityTotal = objectivePriorityTotal(configuredObjectives.filter(hasConfiguredObjectiveEntry))
    const configuredForm = formConfig.appraisal_form || {}
    const showEnglishSections = isConfiguredRegionalForm(formConfig)
    const employeeRatingField = configuredForm.employee_overall_rating_field || 'employee_overall_rating'
    const managerRatingField = configuredForm.overall_rating_field || 'manager_overall_rating'
    const nextYearConfig = formConfig.next_year_objectives || {}
    const nextYearPriorityTotal = objectivePriorityTotal(nextYearObjectives.filter(hasConfiguredObjectiveEntry))
    const maximumNextYearObjectives = nextYearConfig.maximum_rows
    const employeeInformation = formConfig.employee_information || {}
    const employeeInformationFields = (employeeInformation.fields?.length
      ? employeeInformation.fields
      : [
        { key: 'employee_name', label: 'Employee Name' },
        { key: 'employee_number', label: 'Employee Number' },
        { key: 'employee_position', label: 'Employee Position' },
        { key: 'manager_name', label: 'Manager Name' },
        { key: 'department_name', label: 'Department Name' }
      ]).filter(field => field.key !== 'department_code')
    const employeeInformationValues = {
      employee_name: personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template),
      employee_number: appraisal.employee_company_id,
      employee_position: appraisal.employee_position,
      manager_name: appraisal.manager_name,
      department_name: appraisal.department_name,
      performance_period: `${formatDate(appraisal.effective_review_period_start_date)} - ${formatDate(appraisal.effective_review_period_end_date)}`
    }
    const hasAchievementExamples = Object.prototype.hasOwnProperty.call(
      configuredForm.objective_row || {},
      'achievement_examples'
    ) || /(^|_)tti$/.test(appraisal.session_template || '') ||
      configuredObjectives.some(row => Object.prototype.hasOwnProperty.call(row, 'achievement_examples'))
    return (
      <div className='tti-appraisal-layout' style={businessUnitThemeVars(theme)}>
        <div className='tti-appraisal-form' style={{ flex: '1 1 0', minWidth: 0, maxWidth: 1600, margin: '0 auto', padding: '20px' }}>
          <div style={{ marginBottom: 8 }}>
            <BackButton iconOnly showOnlyWhenCanGoBack />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              {theme.logoUrl && <img src={theme.logoUrl} alt={appraisal.session_template_name || 'Template logo'} style={{ display: 'block', maxHeight: 64, maxWidth: 180, objectFit: 'contain', marginBottom: 12 }} />}
              <Title level={2} className='tti-page-title' style={{ marginBottom: 4 }}>{appraisal.session_display_title || '2026 Performance Appraisal'}</Title>
              <Text type='secondary' style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong>{formConfig.employee_information?.employee_label || 'Employee'}:</strong> {personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}
              </Text>
            </div>
          </div>

          {translationReview && (
            <ManagerFeedback
              appraisal={appraisal}
              formData={formData}
              formStructure={formStructure}
              objectives={configuredObjectives}
              formConfig={formConfig}
              ratingDisplay={value => getConfiguredRatingDisplay(value, formConfig)}
            />
          )}

          {!showEnglishSections && formConfig.appraisal_process?.show_rating_scale_table !== true && (
            <Collapse
              className='tti-collapsible-form-card'
              style={{ marginBottom: 12 }}
              expandIconPlacement='end'
              items={[{
                key: 'rating-guide',
                label: <Space><InfoCircleOutlined /><Text strong>Rating Scale Guide</Text><Text type='secondary'>View scoring definitions</Text></Space>,
                children: <RatingGuideDescription source={formConfig} />
              }]}
            />
          )}

          <Collapse
            className='tti-collapsible-form-card'
            style={{ marginBottom: 24 }}
            expandIconPlacement='end'
            items={[{
              key: 'employee-information',
              label: (
                <Space wrap>
                  <Text strong>{employeeInformation.title || 'Employee Information'}</Text>
                  <Text type='secondary'>
                    {[personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template), appraisal.employee_position, appraisal.department_name].filter(Boolean).join(' · ')}
                  </Text>
                </Space>
              ),
              children: (
                <Row gutter={[24, 16]} className='tti-collapsible-fields'>
                  {showEnglishSections
                    ? (
                      <>
                        {employeeInformationFields.map(field => (
                          <Col span={8} key={field.key}>
                            <Text type='secondary' style={{ fontSize: 12 }}>{field.label}</Text>
                            <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                              {employeeInformationValues[field.key] || '--'}
                            </div>
                          </Col>
                        ))}
                      </>
                    )
                    : (
                      <>
                        <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Employee's Name</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}</div></Col>
                        <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Employee's Staff Number</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{appraisal.employee_company_id || '--'}</div></Col>
                        <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Position</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{appraisal.employee_position || '—'}</div></Col>
                        <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Performance Period</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{formatDate(appraisal.effective_review_period_start_date)} - {formatDate(appraisal.effective_review_period_end_date)}</div></Col>
                        <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Approver</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{firstApproverName}</div></Col>
                        <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Department Name</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{appraisal.department_name || '—'}</div></Col>
                        {appraisal.session_organization_structure !== 'department_only' && (
                          <Col span={8}><Text type='secondary' style={{ fontSize: 12 }}>Business Unit</Text><div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>{appraisal.business_unit_name || '—'}</div></Col>
                        )}
                      </>
                    )}
                </Row>
              )
            }]}
          />

          <Card
            className='tti-appraisal-section-card'
            style={{ marginBottom: 24 }}
            title={<Text style={{ color: '#FFF', whiteSpace: 'pre-line' }}>{formConfig.objectives?.title || 'I. Appraisal Year Objectives'}</Text>}
          >
            <Text style={{ display: 'block', marginBottom: 16, color: 'var(--bu-ink)' }}>
              {formConfig.objectives?.description || 'List key objectives and prioritize using percentage assigned. As a percent of total job, the priority total should add up to 100%.'}
            </Text>

            <div style={{ marginBottom: 12, borderBottom: '1px solid var(--tti-light-grey)' }}>
              <Row gutter={8}>
                <Col span={4}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16, whiteSpace: 'pre-line' }}>{formConfig.objectives?.priority_label || 'Priority %'}</div></Col>
                <Col span={hasAchievementExamples ? 10 : 20}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16, whiteSpace: 'pre-line' }}>{formConfig.objectives?.objective_label || 'Annual Performance Objectives'}</div></Col>
                {hasAchievementExamples && (
                  <Col span={10}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16, whiteSpace: 'pre-line' }}>{formConfig.objectives?.achievement_examples_label || 'Examples of achievement'}</div></Col>
                )}
              </Row>
            </div>

            {configuredObjectives.map((objective, index) => (
              <div key={index} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: index < configuredObjectives.length - 1 ? '1px solid var(--neutral-grey2)' : 'none' }}>
                <Row gutter={8} align='top'>
                  <Col span={4}>
                    <Input
                      type='number'
                      min={0}
                      max={100}
                      step={1}
                      value={objective.priority}
                      onChange={e => {
                        if (isWholeNumberInput(e.target.value)) updateObjective(index, 'priority', e.target.value)
                      }}
                      disabled={!isEmployeeInitialEditable}
                      suffix='%'
                    />
                  </Col>
                  <Col span={hasAchievementExamples ? 10 : 20}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <TextArea
                        rows={2}
                        value={objective.objective}
                        onChange={e => updateObjective(index, 'objective', e.target.value)}
                        disabled={!isEmployeeInitialEditable}
                        placeholder='Describe objective...'
                        style={{ fontSize: 16, flex: 1 }}
                      />
                      {!hasAchievementExamples && isEmployeeInitialEditable && configuredObjectives.length > 1 && (
                        <Popconfirm title='Remove this objective?' onConfirm={() => removeObjectiveRow(index)} okText='Remove' okButtonProps={{ danger: true }}>
                          <Button type='text' danger icon={<DeleteOutlined />} aria-label={`Remove objective ${index + 1}`} />
                        </Popconfirm>
                      )}
                    </div>
                  </Col>
                  {hasAchievementExamples && (
                    <Col span={10}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <TextArea
                          rows={2}
                          value={objective.achievement_examples || ''}
                          onChange={e => updateObjective(index, 'achievement_examples', e.target.value)}
                          disabled={!isEmployeeInitialEditable}
                          placeholder='List out examples of achievement...'
                          style={{ fontSize: 16, flex: 1 }}
                        />
                        {isEmployeeInitialEditable && configuredObjectives.length > 1 && (
                          <Popconfirm title='Remove this objective?' onConfirm={() => removeObjectiveRow(index)} okText='Remove' okButtonProps={{ danger: true }}>
                            <Button type='text' danger icon={<DeleteOutlined />} aria-label={`Remove objective ${index + 1}`} />
                          </Popconfirm>
                        )}
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            ))}
            {isEmployeeInitialEditable && (
              <div style={{ marginBottom: 12 }}>
                <Button size='large' className='tti-btn-primary-grey' icon={<PlusOutlined />} onClick={() => setObjectives(prev => [...prev, { ...getConfiguredObjectiveRow(formConfig) }])}>Add Objective</Button>
              </div>
            )}

            <Text type={Math.abs(configuredPriorityTotal - 100) < 0.01 ? 'success' : 'danger'} strong>
              Total Priority: {configuredPriorityTotal}% {Math.abs(configuredPriorityTotal - 100) < 0.01 ? '✓' : '(must equal 100%)'}
            </Text>
          </Card>

          {showEnglishSections && (
            <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF', whiteSpace: 'pre-line' }}>{formConfig.employee_comments?.title || "II. Employee's Comments"}</Text>} style={{ marginBottom: 24 }}>
              {formConfig.employee_comments?.description &&
                formConfig.employee_comments.description !== formConfig.employee_comments.title && (
                  <Text type='secondary' style={{ display: 'block', marginBottom: 8, whiteSpace: 'pre-line' }}>
                    {formConfig.employee_comments.description}
                  </Text>
                )}
              <TextArea
                rows={4}
                value={employeeOverallComment}
                onChange={e => setEmployeeOverallComment(e.target.value)}
                disabled={!isEmployeeInitialEditable}
                placeholder='Employee comments...'
              />
            </Card>
          )}

          {showEnglishSections && (
            <MilwaukeeAsiaEnglishSections
              formData={formData}
              formConfig={formConfig}
              mode={translationReview ? 'translation' : (isEmployeeInitialEditable ? 'employee' : 'read')}
              onChange={setFormData}
              showDevelopmentPlan={false}
              showManagerFields={translationReview}
              cultureSide={translationReview ? 'both' : 'employee'}
              showValidationErrors={showValidationErrors}
            />
          )}

          {!showEnglishSections && isConfiguredFormSectionEnabled(formConfig, 'employee', 'performance_factors', false) && (
            <PerformanceFactorsCard
              formStructure={formStructure}
              formConfig={formConfig}
              getFieldValue={getFieldValue}
              updateField={updateField}
              getQuestionRatingOptions={() => getConfiguredFactorRatingOptions(formConfig)}
              getRatingOptionLabel={option => ratingOptionText(option)}
              getEmployeeRatingOptionLabel={option => ratingOptionText(option)}
              getRatingValueDisplay={value => {
                const option = getConfiguredFactorRatingOptions(formConfig).find(item => item.value === value)
                return option ? ratingOptionText(option) : value
              }}
              editableSide={translationReview ? 'both' : (isEmployeeInitialEditable ? 'employee' : null)}
              visibleSide={translationReview ? 'both' : 'employee'}
            />
          )}

          {!showEnglishSections && (
            <Card className='tti-appraisal-section-card' title={<Text style={{ whiteSpace: 'pre-line', color: '#FFF' }}>{formConfig.employee_comments?.title || "II. Employee's Comments"}</Text>} style={{ marginBottom: 24 }}>
              <Text type='secondary' style={{ display: 'block', marginBottom: 8, whiteSpace: 'pre-line' }}>
                {formConfig.employee_comments?.description || 'This space is for the employee to make any comments he/she wishes.'}
              </Text>
              <TextArea
                rows={4}
                value={employeeOverallComment}
                onChange={e => setEmployeeOverallComment(e.target.value)}
                disabled={!isEmployeeInitialEditable}
                placeholder='Employee comments...'
              />
            </Card>
          )}

          {showEnglishSections && (
            <Card
              className='tti-appraisal-section-card'
              title={<Text strong style={{ whiteSpace: 'pre-line', color: '#FFF' }}>{nextYearConfig.title || 'Setting Next Year Objectives'}</Text>}
              style={{ marginBottom: 24 }}
            >
              {nextYearConfig.description && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
                  {nextYearConfig.description}
                </Text>
              )}
              {nextYearConfig.objectives_note && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', whiteSpace: 'pre-line' }}>
                  {renderBoldMarkup(nextYearConfig.objectives_note)}
                </Text>
              )}
              <div style={{ marginBottom: 12, borderBottom: '1px solid var(--tti-light-grey)' }}>
                <Row gutter={8}>
                  <Col span={4}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16, whiteSpace: 'pre-line' }}>{nextYearConfig.priority_label || 'Weighting (%)'}</div></Col>
                  <Col span={20}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16, whiteSpace: 'pre-line' }}>{nextYearConfig.objective_label || 'Annual Performance Objectives'}</div></Col>
                </Row>
              </div>
              {nextYearObjectives.map((objective, index) => (
                <div key={index} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: index < nextYearObjectives.length - 1 ? '1px solid var(--neutral-grey2)' : 'none' }}>
                  <Row gutter={8} align='top'>
                    <Col span={4}>
                      <Input
                        type='number'
                        min={0}
                        max={100}
                        step={1}
                        value={objective.priority}
                        onChange={event => {
                          if (isWholeNumberInput(event.target.value)) updateNextYearObjective(index, 'priority', event.target.value)
                        }}
                        disabled={!isEmployeeInitialEditable}
                        suffix='%'
                      />
                    </Col>
                    <Col span={20}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <TextArea
                          rows={2}
                          value={objective.objective}
                          onChange={event => updateNextYearObjective(index, 'objective', event.target.value)}
                          disabled={!isEmployeeInitialEditable}
                          placeholder='Describe objective...'
                          style={{ fontSize: 16, flex: 1 }}
                        />
                        {isEmployeeInitialEditable && nextYearObjectives.length > 1 && (
                          <Popconfirm title='Remove this objective?' onConfirm={() => removeNextYearObjectiveRow(index)} okText='Remove' okButtonProps={{ danger: true }}>
                            <Button type='text' danger icon={<DeleteOutlined />} aria-label={`Remove next-year objective ${index + 1}`} />
                          </Popconfirm>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
              {isEmployeeInitialEditable && (!maximumNextYearObjectives || nextYearObjectives.length < maximumNextYearObjectives) && (
                <div style={{ marginBottom: 12 }}>
                  <Button
                    size='large'
                    className='tti-btn-primary-grey'
                    icon={<PlusOutlined />}
                    onClick={() => setNextYearObjectives(prev => [...prev, { ...getConfiguredObjectiveRow(formConfig) }])}
                  >
                    Add Objective
                  </Button>
                </div>
              )}
              <Text type={Math.abs(nextYearPriorityTotal - 100) < 0.01 ? 'success' : 'danger'} strong>
                Total Weighting: {nextYearPriorityTotal}% {Math.abs(nextYearPriorityTotal - 100) < 0.01 ? '✓' : '(must equal 100%)'}
              </Text>
            </Card>
          )}

          {showEnglishSections && (
            <MilwaukeeAsiaEnglishSections
              formData={formData}
              formConfig={formConfig}
              mode={isEmployeeInitialEditable ? 'employee' : 'read'}
              onChange={setFormData}
              showCulture={false}
              showManagerFields={false}
            />
          )}

          {showEnglishSections && (
            <Card
              className='tti-appraisal-section-card'
              title={<Text style={{ color: '#FFF', whiteSpace: 'pre-line' }}>{formConfig.appraisal_process?.performance_factors_title || formConfig.rating_scale_guide?.title || 'Performance Factors'}</Text>}
              style={{ marginBottom: 24 }}
            >
              <RatingGuideTable source={formConfig} />
            </Card>
          )}

          {showEnglishSections && (
            <Card
              className='tti-appraisal-section-card'
              title={(
                <Text style={{ whiteSpace: 'pre-line', color: '#FFF' }}>
                  {formConfig.appraisal_process?.employee_overall_rating_title ||
                    `${formConfig.appraisal_process?.overall_rating_label || 'Overall Performance Rating'} - ${formConfig.appraisal_process?.employee_evaluation_label || 'Employee Self-Rating'}`}
                </Text>
              )}
              style={{ marginBottom: 24 }}
            >
              {formConfig.appraisal_process?.overall_rating_description !== '' && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 8, whiteSpace: 'pre-line' }}>
                  {formConfig.appraisal_process?.overall_rating_description || 'Select the rating that best describes your overall performance.'}
                </Text>
              )}
              <Select
                style={{ width: '100%' }}
                value={formData[employeeRatingField]}
                onChange={value => setFormData(prev => ({ ...prev, [employeeRatingField]: value }))}
                disabled={!isEmployeeInitialEditable}
                placeholder='Select employee self-rating...'
                allowClear
              >
                {getConfiguredOverallRatingOptions(formConfig).map(option => (
                  <Option key={option.value} value={option.value}>
                    {ratingOptionText(option)}
                  </Option>
                ))}
              </Select>
            </Card>
          )}

          {showEnglishSections && translationReview && (
            <Card
              className='tti-appraisal-section-card'
              title={(
                <Text style={{ color: '#FFF', whiteSpace: 'pre-line' }}>
                  {formConfig.appraisal_process?.manager_overall_rating_title ||
                    `${formConfig.appraisal_process?.overall_rating_label || 'Overall Performance Rating'} - ${formConfig.appraisal_process?.manager_evaluation_label || 'Manager Rating'}`}
                </Text>
              )}
              style={{ marginBottom: 24 }}
            >
              {formConfig.appraisal_process?.overall_rating_description !== '' && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 8, whiteSpace: 'pre-line' }}>
                  {formConfig.appraisal_process?.overall_rating_description || "Select the rating that best describes the employee's overall performance."}
                </Text>
              )}
              <Select
                style={{ width: '100%' }}
                value={formData[managerRatingField]}
                onChange={value => setFormData(prev => ({ ...prev, [managerRatingField]: value }))}
                placeholder='Select manager rating...'
                allowClear
              >
                {getConfiguredOverallRatingOptions(formConfig).map(option => (
                  <Option key={option.value} value={option.value}>
                    {ratingOptionText(option)}
                  </Option>
                ))}
              </Select>
            </Card>
          )}

          {actionBar}
        </div>
        {conversationSidebar}
      </div>
    )
  }

  return (
    <div className='tti-appraisal-layout' style={businessUnitThemeVars(theme)}>
      <div className='tti-appraisal-form' style={{ flex: '1 1 0', minWidth: 0, maxWidth: 1600, margin: '0 auto', padding: '20px' }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton iconOnly showOnlyWhenCanGoBack />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            {theme.logoUrl && <img src={theme.logoUrl} alt={appraisal.session_template_name || 'Template logo'} style={{ display: 'block', maxHeight: 64, maxWidth: 180, objectFit: 'contain', marginBottom: 12 }} />}
            <Title level={2} className='tti-page-title' style={{ marginBottom: 4 }}>{appraisal.session_display_title || '2026 Performance Appraisal'}</Title>
            <Text type='secondary' style={{ fontSize: 14, lineHeight: 1.5 }}>(for Staff Level & above)</Text>
            <br />
            <Text type='secondary' style={{ fontSize: 14, lineHeight: 1.5 }}>
              <strong>Employee:</strong> {personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}
            </Text>
          </div>
        </div>

        {translationReview && (
          <ManagerFeedback
            appraisal={appraisal}
            formData={formData}
            formStructure={formStructure}
            objectives={objectives}
            formConfig={formConfig}
            ratingDisplay={value => usesConfiguredRatings ? getConfiguredRatingDisplay(value, formConfig) : getRatingValueDisplay(value, ratingGuideSource)}
          />
        )}

        {/* Rating Guide */}
        <Collapse
          className='tti-collapsible-form-card'
          style={{ marginBottom: 12 }}
          expandIconPlacement='end'
          items={[{
            key: 'rating-guide',
            label: <Space><InfoCircleOutlined /><Text strong>Rating Scale Guide</Text><Text type='secondary'>View scoring definitions</Text></Space>,
            children: isPrc ? <PrcRatingGuideDescription /> : <RatingGuideDescription source={ratingGuideSource} />
          }]}
        />

        {/* Employee Information */}
        <Collapse
          className='tti-collapsible-form-card'
          style={{ marginBottom: 24 }}
          expandIconPlacement='end'
          items={[{
            key: 'employee-information',
            label: (
              <Space wrap>
                <Text strong>{isPrc ? prcEmployeeInformationTitle(formConfig) : 'Employee Information'}</Text>
                <Text type='secondary'>
                  {[personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template), appraisal.employee_position, appraisal.department_name].filter(Boolean).join(' · ')}
                </Text>
              </Space>
            ),
            children: (
              <Row gutter={[24, 16]} className='tti-collapsible-fields'>
                {isPrc
                  ? prcEmployeeInfoFields.map(field => (
                    <Col span={8} key={field.key}>
                      <Text type='secondary' style={{ fontSize: 12 }}>{field.label}:</Text>
                      <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                        {field.value}
                      </div>
                    </Col>
                  ))
                  : (
                    <>
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Fullname:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Emp.ID:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.employee_company_id || appraisal.employee || '—'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Position:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>—</div>
                      </Col>
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Dept.:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.department_name || '—'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Joined Date ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.employee_date_joined)}
                        </div>
                      </Col>

                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Review Period From ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.effective_review_period_start_date)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12 }}>Review Period To ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.effective_review_period_end_date)}
                        </div>
                      </Col>
                      <Col span={8} />

                      <Col span={8}>
                        <Text type='secondary' style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluator Name:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {personLabel(appraisal.approver_1_first_name ?? appraisal.manager_first_name, appraisal.approver_1_last_name ?? appraisal.manager_last_name, appraisal.approver_1_email ?? appraisal.manager_email, '', appraisal.session_template)}
                        </div>
                      </Col>
                    </>
                  )}
                {appraisal.session_organization_structure !== 'department_only' && (
                  <Col span={8}>
                    <Text type='secondary' style={{ fontSize: 12 }}>Business Unit:</Text>
                    <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                      {appraisal.business_unit_name || '—'}
                    </div>
                  </Col>
                )}
                {!isPrc && (
                  <>
                    <Col span={8}>
                      <Text type='secondary' style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluation Date ({DATE_FORMAT_LABEL}):</Text>
                      <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                        {formatDate(appraisal.manager_submitted_at)}
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type='secondary' style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluator ID:</Text>
                      <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                        {appraisal.manager_company_id || '--'}
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            )
          }]}
        />

        {/* Annual Objectives Appraisal */}
        <Card
          className='tti-appraisal-section-card'
          style={{ marginBottom: 24 }}
          title={<Text style={{ color: '#FFF' }}>{objectivesConfig.title || 'Annual Objectives Appraisal'}</Text>}
        >
          {(appraisal.session_template || appraisal.session_business_unit || '').includes('milwaukee') && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16, color: 'var(--bu-ink)' }}>List and prioritize your key objectives following the S.M.A.R.T principles</Text>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: 'var(--bu-ink)' }}>
                <li><Text strong>S – Specific:</Text> clear and well-defined (What exactly do I want to achieve? Why is it important?)</li>
                <li><Text strong>M – Measurable:</Text> can be tracked using metrics or indicators (How will I measure success? What evidence shows progress?)</li>
                <li><Text strong>A – Achievable:</Text> realistic and attainable (Can I reach this goal with my current skills and resources?)</li>
                <li><Text strong>R – Relevant:</Text> aligns with team, department, or company objectives (Does this goal support broader organizational priorities?)</li>
                <li><Text strong>T – Time-bound:</Text> clear deadline or timeframe for completion (When will I achieve this goal? What are the milestones?)</li>
              </ol>
            </div>
          )}
          <Text style={{ display: 'block', marginBottom: 16, color: 'var(--bu-ink)' }}>
            {isPrc
              ? 'List up to ten SMART performance objectives. Weight total must add up to 100%. Include success indicators for each objective.'
              : (objectivesConfig.description || 'List key objectives and prioritize using percentage assigned. As a percent of total job, and it should add up to 100%.')}
          </Text>

          {/* Header */}
          <div style={{ marginBottom: 12, borderBottom: '1px solid var(--tti-light-grey)' }}>
            <Row gutter={8}>
              <Col span={3}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16 }}>{objectivesConfig.priority_label || '% Priority'}</div></Col>
              <Col span={isPrc ? 6 : 8}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16 }}>{isPrc ? prcObjectives.objective_label : (objectivesConfig.objective_label || 'Annual Performance Objectives')}</div></Col>
              {isPrc && <Col span={6}><div style={{ padding: '8px 4px', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey1)', fontSize: 16 }}>{prcObjectives.success_indicators_label}</div></Col>}
              <Col span={isPrc ? 9 : 13}>
                <div style={{ padding: '6px 0', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey2)', fontSize: 16, marginBottom: 4 }}>Self-Evaluation</div>
                <Row gutter={4}>
                  <Col span={10}><div style={{ padding: '4px 0', fontSize: 16, fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey2)' }}>{objectiveScoreLabel}</div></Col>
                  <Col span={14}><div style={{ padding: '4px 0', fontSize: 16, fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey2)' }}>Comment</div></Col>
                </Row>
              </Col>
            </Row>
          </div>

          {/* Rows */}
          {objectives.map((obj, idx) => (
            <div key={idx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: idx < objectives.length - 1 ? '1px solid var(--neutral-grey2)' : 'none' }}>
              <Row gutter={8} align='top'>
                <Col span={3}>
                  {usesPriorityOptions
                    ? (
                      <Select
                        style={{ width: '100%' }}
                        value={obj.priority || undefined}
                        onChange={value => updateObjective(idx, 'priority', value)}
                        disabled={isReadOnly}
                        placeholder='Select'
                      >
                        {!allowedPriorityValues.has(`${obj.priority}`) && obj.priority !== '' && obj.priority !== null && obj.priority !== undefined && (
                          <Option value={obj.priority}>{obj.priority}% (legacy)</Option>
                        )}
                        {objectivePriorityOptions.map(option => <Option key={option.value} value={option.value}>{option.label || option.value}</Option>)}
                      </Select>
                    )
                    : (
                      <Input
                        type='number'
                        min={0} max={100}
                        step={1}
                        value={obj.priority}
                        onChange={e => {
                          if (isWholeNumberInput(e.target.value)) updateObjective(idx, 'priority', e.target.value)
                        }}
                        disabled={isReadOnly}
                        placeholder='%'
                        suffix='%'
                      />
                    )}
                </Col>
                <Col span={isPrc ? 6 : 8}>
                  <TextArea
                    rows={2}
                    value={obj.objective}
                    onChange={e => updateObjective(idx, 'objective', e.target.value)}
                    disabled={isReadOnly}
                    placeholder='Describe objective...'
                    status={showValidationErrors && !hasConfiguredObjectiveText(obj) && (
                      hasAnnualObjectiveEntry(obj) || (!hasAnyObjectiveText && idx === 0)
                    ) ? 'error' : undefined}
                    style={{ fontSize: 16 }}
                  />
                  {showValidationErrors && !hasConfiguredObjectiveText(obj) && (
                    hasAnnualObjectiveEntry(obj) || (!hasAnyObjectiveText && idx === 0)
                  ) && (
                      <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                        Annual Performance Objective is required.
                      </Text>
                    )}
                </Col>
                {isPrc && (
                  <Col span={6}>
                    <Space direction='vertical' size={4} style={{ width: '100%' }}>
                      {prcSuccessIndicatorLevels.map(level => (
                        <div key={level.key}>
                          <Text type='secondary' style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>{level.label}</Text>
                          <TextArea
                            rows={1}
                            value={getPrcSuccessIndicatorValue(obj, level.key)}
                            onChange={e => updateObjective(idx, prcSuccessIndicatorField(level.key), e.target.value)}
                            disabled={isReadOnly}
                            placeholder={level.label}
                            style={{ fontSize: 16 }}
                          />
                        </div>
                      ))}
                    </Space>
                  </Col>
                )}
                <Col span={isPrc ? 9 : 13}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Row gutter={4} style={{ flex: 1 }}>
                      <Col span={10}>
                        {usesConfiguredObjectiveRatings
                          ? (
                            <Select
                              style={{ width: '100%' }}
                              value={obj.self_score === '' || obj.self_score === null ? undefined : Number(obj.self_score)}
                              onChange={value => updateObjective(idx, 'self_score', value)}
                              disabled={isReadOnly}
                              placeholder='Rating'
                              allowClear
                              status={showValidationErrors && hasConfiguredObjectiveText(obj) && (
                                obj.self_score === null || obj.self_score === undefined || obj.self_score === ''
                              ) ? 'error' : undefined}
                            >
                              {getConfiguredRatingOptions(formConfig, obj.self_score).map(option => (
                                <Option key={option.value} value={option.value}>{getEmployeeRatingOptionLabel(option)}</Option>
                              ))}
                            </Select>
                          )
                          : (
                            <Input
                              type='number'
                              min={1}
                              max={objectiveScoreMax}
                              step='0.01'
                              style={{ width: '100%' }}
                              value={obj.self_score}
                              onChange={e => {
                                if ((allowDecimalObjectiveScores ? isDecimalInput : isWholeNumberInput)(e.target.value)) {
                                  updateObjective(idx, 'self_score', e.target.value)
                                }
                              }}
                              onBlur={e => updateObjective(
                                idx,
                                'self_score',
                                normalizeObjectiveScore(e.target.value, objectiveScoreMax, allowDecimalObjectiveScores)
                              )}
                              disabled={isReadOnly}
                              placeholder='Score'
                            />
                          )}
                        {showValidationErrors && hasConfiguredObjectiveText(obj) && (
                          obj.self_score === null || obj.self_score === undefined || obj.self_score === ''
                        ) && (
                            <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                              Self-evaluation rating is required.
                            </Text>
                          )}
                      </Col>
                      <Col span={14}>
                        <TextArea
                          rows={2}
                          value={obj.self_comment}
                          onChange={e => updateObjective(idx, 'self_comment', e.target.value)}
                          disabled={isReadOnly}
                          placeholder='Comment...'
                          style={{ fontSize: 16 }}
                        />
                      </Col>
                    </Row>
                    {!isReadOnly && objectives.length > 1 && (
                      <Popconfirm title='Remove this objective?' onConfirm={() => removeObjectiveRow(idx)} okText='Remove' okButtonProps={{ danger: true }}>
                        <Button type='text' danger icon={<DeleteOutlined />} aria-label={`Remove objective ${idx + 1}`} />
                      </Popconfirm>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          ))}
          {!isReadOnly && (
            <div style={{ marginBottom: 12 }}>
              <Button size='large' className='tti-btn-primary-grey' icon={<PlusOutlined />} onClick={addObjectiveRow}>Add Objective</Button>
            </div>
          )}

          {/* Priority total indicator */}
          {!usesPriorityOptions && (
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <Text type={Math.abs(totalPriority - 100) < 0.01 ? 'success' : 'danger'} strong>
                Total Priority: {totalPriority}% {Math.abs(totalPriority - 100) < 0.01 ? '✓' : '(must equal 100%)'}
              </Text>
            </div>
          )}

          {/* Averages — TTI Vietnam Form Card footer bar (Figma 1193:30786):
            TTI Blue Light surface, Grey top border, Bold 24 TTI Blue label
            and white data boxes */}
          <div style={{ marginTop: 16, borderTop: '1px solid var(--tti-light-grey)', background: 'var(--tti-blue-light)', padding: '20px 24px' }}>
            {showObjectiveAverageScore && (
              <Row gutter={8} align='middle'>
                <Col span={8}><Text strong style={{ fontSize: 24, lineHeight: 1.2, color: 'var(--tti-blue)' }}>Average Score</Text></Col>
                <Col span={16}>
                  <div style={{ textAlign: 'center', padding: 10, background: 'var(--neutral-white)' }}>
                    <Text strong style={{ display: 'block', fontSize: 24, lineHeight: 1.2, color: 'var(--tti-info)' }}>{objectiveSelfAvg || '--'}</Text>
                    {objectiveSelfAvg && (
                      <Text type='secondary' style={{ display: 'block', fontSize: 14, marginTop: 4 }}>
                        {isPrc ? prcEvaluationLevel(objectiveSelfAvg) : getRatingCategory(objectiveSelfAvg)?.rating}
                      </Text>
                    )}
                  </div>
                </Col>
              </Row>
            )}
            {objectiveSummaryEnabled && (
              <>
                <SuggestedRatingBand items={[{
                  label: 'Calculated Suggested Rating',
                  value: employeeObjectiveSuggestedRating
                }]} />
                <Row gutter={8} align='middle' style={{ marginTop: 12 }}>
                  <Col span={8}><Text strong>Overall Rating of Objective Appraisal (manual)</Text></Col>
                  <Col span={16}>
                    <Select
                      size='large'
                      style={{ width: '100%' }}
                      value={formData[employeeObjectiveRatingField] === null || formData[employeeObjectiveRatingField] === undefined
                        ? undefined
                        : Number(formData[employeeObjectiveRatingField])}
                      onChange={value => setFormData(prev => ({ ...prev, [employeeObjectiveRatingField]: value }))}
                      disabled={isReadOnly}
                      placeholder='Select rating'
                      allowClear
                      status={showValidationErrors &&
                        templateCode === 'vietnam_milwaukee' &&
                        objectiveSummaryConfig.required &&
                        !formData[employeeObjectiveRatingField]
                        ? 'error'
                        : undefined}
                    >
                      {getConfiguredRatingOptions(formConfig, formData[employeeObjectiveRatingField]).map(option => (
                        <Option key={option.value} value={option.value}>{getConfiguredRatingFullLabel(option)}</Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </Card>

        {isCheckboxFlow
          ? (
            <PerformanceFactorsCard
              formStructure={formStructure}
              formConfig={formConfig}
              getFieldValue={getFieldValue}
              updateField={updateField}
              getQuestionRatingOptions={getQuestionRatingOptions}
              getRatingOptionLabel={getQuestionRatingLabel}
              getEmployeeRatingOptionLabel={getEmployeeRatingOptionLabel}
              getRatingValueDisplay={value => usesConfiguredRatings ? getConfiguredRatingDisplay(value, formConfig) : getRatingValueDisplay(value, ratingGuideSource)}
              editableSide={translationReview ? 'both' : (isReadOnly ? null : 'employee')}
              visibleSide={translationReview ? 'both' : 'employee'}
            />
          )
          : formStructure.map((category, catIdx) => (
            <Card
              key={category.id}
              className='tti-appraisal-section-card'
              style={{ marginBottom: 24 }}
              title={(
                <div>
                  <Text style={{ color: '#FFF' }}>{category.title}</Text>
                  {category.description && (
                    <Text type='secondary' style={{ display: 'block', marginTop: 4, whiteSpace: 'normal', color: '#FFF' }}>
                      {category.description}
                    </Text>
                  )}
                </div>
              )}
            >
              {/* Two-tiered header */}
              <div style={{ marginBottom: 16, borderBottom: '2px solid #d9d9d9' }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ padding: '8px 0', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--neutral-grey0)', fontSize: 16 }}>
                      Question
                    </div>
                  </Col>
                  <Col span={16}>
                    <div style={{ padding: '8px 0', fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--bu-primary-bg)', borderLeft: '2px solid var(--bu-primary-border)', borderRight: '2px solid var(--bu-primary-border)', fontSize: 16 }}>
                      {isPrc ? `Self-Evaluation ${objectiveScoreLabel}` : 'Self-Evaluation'}
                    </div>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8} />
                  <Col span={16}>
                    <Row gutter={8}>
                      <Col span={singleCategoryComment ? 24 : 10}>
                        <div style={{ padding: '4px 0', fontSize: 16, fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--bu-primary-bg)' }}>
                          Rating
                        </div>
                      </Col>
                      {!singleCategoryComment && (
                        <Col span={14}>
                          <div style={{ padding: '4px 0', fontSize: 16, fontWeight: 700, lineHeight: 1.5, textAlign: 'center', background: 'var(--bu-primary-bg)' }}>
                            Comments
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </div>

              {/* Questions */}
              {category.questions.map((question, qIdx) => (
                <div key={question.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: qIdx < category.questions.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <Row gutter={16} align='top'>
                    <Col span={8}>
                      <Text style={{ fontSize: 16, lineHeight: '1.5' }}>
                        {qIdx + 1}. {question.text}
                      </Text>
                    </Col>
                    <Col span={16}>
                      <Row gutter={8}>
                        <Col span={singleCategoryComment ? 24 : 10}>
                          {isPrc
                            ? (
                              <Input
                                type='number'
                                min={1}
                                max={10}
                                step={1}
                                value={getFieldValue(category.id, question.id, 'employee_rating')}
                                onChange={(e) => {
                                  if (isWholeNumberInput(e.target.value)) updateField(category.id, question.id, 'employee_rating', e.target.value)
                                }}
                                onBlur={e => updateField(category.id, question.id, 'employee_rating', normalizeObjectiveScore(e.target.value, 10))}
                                disabled={isReadOnly}
                                placeholder='Score'
                                status={showValidationErrors && !getFieldValue(category.id, question.id, 'employee_rating') ? 'error' : undefined}
                              />
                            )
                            : (
                              <Select
                                style={{ width: '100%' }}
                                value={getFieldValue(category.id, question.id, 'employee_rating')}
                                onChange={(v) => updateField(category.id, question.id, 'employee_rating', v)}
                                disabled={isReadOnly}
                                placeholder='Select rating...'
                                allowClear
                                optionLabelProp='label'
                                status={showValidationErrors && !getFieldValue(category.id, question.id, 'employee_rating') ? 'error' : undefined}
                              >
                                {getQuestionRatingOptions(getFieldValue(category.id, question.id, 'employee_rating')).map(opt => (
                                  <Option key={opt.value} value={opt.value} label={getEmployeeRatingOptionLabel(opt)}>
                                    {getEmployeeRatingOptionLabel(opt)}
                                  </Option>
                                ))}
                              </Select>
                            )}
                          {showValidationErrors && !getFieldValue(category.id, question.id, 'employee_rating') && (
                            <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                              Rating is required.
                            </Text>
                          )}
                        </Col>
                        {!singleCategoryComment && (
                          <Col span={14}>
                            <TextArea
                              rows={2}
                              value={getFieldValue(category.id, question.id, 'employee_comments')}
                              onChange={(e) => updateField(category.id, question.id, 'employee_comments', e.target.value)}
                              disabled={isReadOnly}
                              placeholder='Comments...'
                              style={{ fontSize: 16 }}
                            />
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </div>
              ))}

              {singleCategoryComment && category.questions.some(question => question.id === categoryCommentQuestionKey) && (
                <div style={{ marginTop: 16 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {categoryCommentLabel}{categoryCommentRequired ? ' *' : ''}
                  </Text>
                  <TextArea
                    rows={3}
                    value={getFieldValue(category.id, categoryCommentQuestionKey, 'employee_comments')}
                    onChange={(e) => updateField(category.id, categoryCommentQuestionKey, 'employee_comments', e.target.value)}
                    disabled={isReadOnly}
                    placeholder={`${categoryCommentLabel}...`}
                    status={showValidationErrors && categoryCommentRequired && !String(getFieldValue(category.id, categoryCommentQuestionKey, 'employee_comments') || '').trim() ? 'error' : undefined}
                    style={{ fontSize: 16 }}
                  />
                  {showValidationErrors && categoryCommentRequired && !String(getFieldValue(category.id, categoryCommentQuestionKey, 'employee_comments') || '').trim() && (
                    <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                      {categoryCommentLabel} is required.
                    </Text>
                  )}
                </div>
              )}

              {/* Category Averages */}
              <CalculatedRatingBand label={categoryAverageLabel} items={[{
                value: showCategoryAverageAsRating
                  ? calculateCategorySuggestedRating(category.id, 'employee_rating')
                  : calculateCategoryAverage(category.id, 'employee_rating'),
                detail: !showCategoryAverageAsRating && calculateCategoryAverage(category.id, 'employee_rating')
                  ? (isPrc
                    ? prcEvaluationLevel(calculateCategoryAverage(category.id, 'employee_rating'))
                    : getRatingCategory(calculateCategoryAverage(category.id, 'employee_rating'))?.rating)
                  : null
              }]} />
            </Card>
          ))}

        <Card
          className='tti-appraisal-section-card'
          style={{ marginBottom: 24 }}
          title={<Text style={{ color: '#FFF' }}>{competencySummaryTitle}{competencySummarySubtitle && <><br />({competencySummarySubtitle})</>}</Text>}
        >
          {showCompetencySummarySubtext && (
            <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
              Calculated as the average of all competency category ratings.
            </Text>
          )}
          <Row gutter={16}>
            <Col span={24}>
              <SuggestedRatingBand items={[{
                label: 'Calculated Suggested Rating',
                value: showCategoryAverageAsRating ? employeeCompetencySuggestedRating : overallEmployeeCategoryAvg,
                detail: !showCategoryAverageAsRating && overallEmployeeCategoryAvg
                  ? (isPrc ? prcEvaluationLevel(overallEmployeeCategoryAvg) : getRatingCategoryDisplay(overallEmployeeCategoryAvg))
                  : null
              }]} />
            </Col>
          </Row>
          {competencyManualRating.enabled && (
            <div style={{ marginTop: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>{competencySummaryTitle} (manual)</Text>
              <Select
                size='large'
                style={{ width: '100%' }}
                value={formData[employeeCompetencyRatingField] == null ? undefined : Number(formData[employeeCompetencyRatingField])}
                onChange={value => setFormData(prev => ({ ...prev, [employeeCompetencyRatingField]: value }))}
                disabled={isReadOnly}
                placeholder='Select rating'
                status={showValidationErrors &&
                  templateCode === 'vietnam_milwaukee' &&
                  competencyManualRating.required &&
                  !formData[employeeCompetencyRatingField]
                  ? 'error'
                  : undefined}
              >
                {getConfiguredRatingOptions(formConfig, formData[employeeCompetencyRatingField]).map(option => (
                  <Option key={option.value} value={option.value}>{getConfiguredRatingFullLabel(option)}</Option>
                ))}
              </Select>
            </div>
          )}
        </Card>

        <Card
          className='tti-appraisal-section-card'
          style={{ marginBottom: 24 }}
          title={<Text style={{ color: '#FFF' }}>{overallPerformanceTitle}</Text>}
        >
          {showOverallSummarySubtext && (
            <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
              Calculated equally from the weighted Objectives score and Competency score.
            </Text>
          )}
          <SuggestedRatingBand items={[{
            label: 'Calculated Suggested Rating',
            value: showCategoryAverageAsRating ? employeeOverallSuggestedRating : overallEmployeeAverage,
            detail: !showCategoryAverageAsRating && overallEmployeeAverage
              ? (isPrc ? prcEvaluationLevel(overallEmployeeAverage) : getRatingCategoryDisplay(overallEmployeeAverage))
              : null
          }]} />
          {overallManualRating.enabled && (
            <div style={{ marginTop: 12 }}>
              <Text
                strong
                type={isOverallPerformanceRatingMissing ? 'danger' : undefined}
                style={{ display: 'block', marginBottom: 6 }}
              >
                {overallPerformanceTitle} (manual)
              </Text>
              <Select
                size='large'
                style={{ width: '100%' }}
                value={formData[employeeOverallRatingField] == null ? undefined : Number(formData[employeeOverallRatingField])}
                onChange={value => setFormData(prev => ({ ...prev, [employeeOverallRatingField]: value }))}
                disabled={isReadOnly}
                placeholder='Select rating'
                status={isOverallPerformanceRatingMissing ? 'error' : undefined}
              >
                {getConfiguredRatingOptions(formConfig, formData[employeeOverallRatingField]).map(option => (
                  <Option key={option.value} value={option.value}>{getConfiguredRatingFullLabel(option)}</Option>
                ))}
              </Select>
              {isOverallPerformanceRatingMissing && (
                <Text type='danger' style={{ display: 'block', marginTop: 4 }}>
                  {overallPerformanceTitle} is required.
                </Text>
              )}
              {templateCode === 'vietnam_milwaukee' && requiresOverallRatingJustification && (
                <div style={{ marginTop: 12 }}>
                  <Text strong style={{ display: 'block', marginBottom: 6 }}>
                    Rating Justification *
                  </Text>
                  <TextArea
                    rows={3}
                    value={formData.employee_overall_rating_justification || ''}
                    onChange={event => setFormData(prev => ({ ...prev, employee_overall_rating_justification: event.target.value }))}
                    disabled={isReadOnly}
                    placeholder={`Required — minimum ${justificationMinLength} characters`}
                    status={showValidationErrors && requiresOverallRatingJustification &&
                      (formData.employee_overall_rating_justification || '').trim().length < justificationMinLength
                      ? 'error'
                      : undefined}
                    showCount
                  />
                </div>
              )}
            </div>
          )}
        </Card>

        {isCheckboxFlow && (
          <CultureValuesCheckboxes
            formData={formData}
            formConfig={formConfig}
            disabled={isReadOnly}
            onChange={value => setCheckboxFlowData(setFormData, value)}
          />
        )}

        {isPrc && (
          <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Individual Development Plan (IDP)</Text>} style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              {prcIdpFieldRows.filter(([field]) => field !== 'supervisor_year_end_suggestion').map(([field, label]) => {
                return (
                  <Col span={12} key={field}>
                    <Text strong>{label}</Text>
                    <TextArea
                      rows={4}
                      value={prcIdp[field] || ''}
                      onChange={e => updatePrcIdp(field, e.target.value)}
                      disabled={isReadOnly}
                      placeholder={label}
                      style={{ marginTop: 8 }}
                    />
                  </Col>
                )
              })}
            </Row>
          </Card>
        )}

        {/* Overall Comments Section */}
        <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Overall Comments</Text>} style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={24}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Employee Overall Comment</Text>
              </div>
              <TextArea
                rows={4}
                value={employeeOverallComment}
                onChange={(e) => setEmployeeOverallComment(e.target.value)}
                disabled={isReadOnly}
                placeholder='Please provide your overall comments...'
              />
            </Col>
          </Row>
        </Card>

        {/* Action Buttons */}
        {actionBar}

        {isReadOnly && (
          <Alert
            message='This appraisal has already been submitted'
            description='You can view your responses but cannot make changes.'
            type='info'
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
      </div>
      {conversationSidebar}
    </div>
  )
}
